/**
 * Parasolune — cart shipping helpers.
 * Wash-line-only carts: R0. Parasols: Western Cape free, other SA provinces R350. 6+ parasol orders quoted separately.
 * enabled:false — never add solara-shipping; still removes leftover fee lines + sets bulk attribute.
 */
(function () {
  const SOURCE = 'solara-transport';

  function normalizeId(id) {
    return id == null || id === '' ? null : String(id);
  }

  function readConfig() {
    const el = document.getElementById('solara-transport-config');
    if (!el) return null;
    try {
      const config = JSON.parse(el.textContent);
      const shippingIds = Object.values(config.shippingVariants || {})
        .map(normalizeId)
        .filter(Boolean);
      const legacyIds = (config.legacyTransportVariantIds || []).map(normalizeId).filter(Boolean);
      config.allTransportVariantIds = [...new Set([...shippingIds, ...legacyIds])];
      config.shippingVariants = Object.fromEntries(
        Object.entries(config.shippingVariants || {}).map(([tier, id]) => [tier, normalizeId(id)])
      );
      config.bulkOrderMinQty = config.bulkOrderMinQty || 6;
      config.bulkOrderAttributeKey = config.bulkOrderAttributeKey || 'Shipping';
      config.bulkOrderAttributeValue =
        config.bulkOrderAttributeValue || 'Excluded — will be costed separately before dispatch';
      return config;
    } catch (error) {
      console.warn('Parasolune shipping: invalid config', error);
      return null;
    }
  }

  function freeDeliveryValues(config) {
    if (Array.isArray(config?.freeDeliveryAttributeValues) && config.freeDeliveryAttributeValues.length) {
      return config.freeDeliveryAttributeValues;
    }
    return [config?.freeDeliveryAttributeValue || 'Western Cape', 'Cape Town'];
  }

  function isFreeDelivery(cart, config) {
    if (!config?.freeDeliveryEnabled) return false;
    const key = config.freeDeliveryAttributeKey || 'Delivery zone';
    const zone = cart?.attributes?.[key] || '';
    return freeDeliveryValues(config).includes(zone);
  }

  function isBulkOrder(qty, config) {
    return qty >= (config.bulkOrderMinQty || 6);
  }

  /** @returns {'nationwide'|null} */
  function getTier(quantity, config) {
    if (quantity <= 0 || isBulkOrder(quantity, config)) return null;
    if (quantity >= 1 && quantity <= 5) return 'nationwide';
    return null;
  }

  function itemHandle(item) {
    if (!item?.url) return '';
    const match = item.url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function isTransportItem(item, config) {
    if (!item) return false;
    const variantId = normalizeId(item.variant_id);
    if (config.allTransportVariantIds.includes(variantId)) return true;
    if (item.product_type === 'Transport Fee') return true;
    const handle = itemHandle(item);
    return (
      handle === config.shippingHandle ||
      handle === 'solara-washline-transport' ||
      handle === 'solara-parasol-delivery'
    );
  }

  function isShippableItem(item, config) {
    if (isTransportItem(item, config)) return false;
    const handle = itemHandle(item);
    const productType = (item.product_type || '').toLowerCase();

    if ((config.shippableHandles || []).includes(handle)) return true;
    if (productType.includes('wash line')) return true;
    if (productType.includes('parasol')) return true;
    return false;
  }

  function isWashlineItem(item, config) {
    const handle = itemHandle(item);
    if ((config.washlineHandles || []).includes(handle)) return true;
    return (item.product_type || '').toLowerCase().includes('wash line');
  }

  function isParasolItem(item, config) {
    const handle = itemHandle(item);
    if ((config.parasolHandles || []).includes(handle)) return true;
    const type = (item.product_type || '').toLowerCase();
    return type.includes('parasol') || type.includes('umbrella');
  }

  /** Wash-line-only carts ship at R0, so they are not a bulk quote. */
  function countForPolicy(cart, config) {
    const items = (cart.items || []).filter((item) => isShippableItem(item, config));
    const washQty = items.filter((item) => isWashlineItem(item, config)).reduce((total, item) => total + item.quantity, 0);
    const parasolQty = items.filter((item) => isParasolItem(item, config)).reduce((total, item) => total + item.quantity, 0);
    if (washQty > 0 && parasolQty === 0) return 0;
    return items.reduce((total, item) => total + item.quantity, 0);
  }

  function countShippable(cart, config) {
    return (cart.items || []).reduce((total, item) => {
      if (!isShippableItem(item, config)) return total;
      return total + item.quantity;
    }, 0);
  }

  function shippingFeeTotal(cart, config) {
    return (cart.items || []).reduce((total, item) => {
      if (!isTransportItem(item, config)) return total;
      return total + item.final_line_price;
    }, 0);
  }

  function formatMoney(cents, currency) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    const amount = (cents / 100).toFixed(2);
    return currency === 'ZAR' ? `R ${amount}` : `${amount} ${currency || ''}`.trim();
  }

  function desiredVariantMap(config, cart) {
    const desired = {};
    config.allTransportVariantIds.forEach((id) => {
      desired[id] = 0;
    });

    const qty = countForPolicy(cart, config);
    const tier = getTier(qty, config);

    // Only add solara-shipping when explicitly enabled (checkout rates are preferred).
    if (
      config.enabled &&
      tier &&
      config.shippingVariants?.[tier] &&
      !isFreeDelivery(cart, config)
    ) {
      desired[config.shippingVariants[tier]] = 1;
    }

    return { desired, qty, tier };
  }

  function currentVariantMap(cart, config) {
    const current = {};
    config.allTransportVariantIds.forEach((id) => {
      current[id] = 0;
    });
    (cart.items || []).forEach((item) => {
      const variantId = normalizeId(item.variant_id);
      if (config.allTransportVariantIds.includes(variantId)) {
        current[variantId] = item.quantity;
      }
    });
    return current;
  }

  function mapsEqual(a, b) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
      if ((a[key] || 0) !== (b[key] || 0)) return false;
    }
    return true;
  }

  function targetBulkAttribute(config, qty) {
    return isBulkOrder(qty, config) ? config.bulkOrderAttributeValue : '';
  }

  function currentBulkAttribute(cart, config) {
    return cart?.attributes?.[config.bulkOrderAttributeKey] || '';
  }

  function bulkAttributeNeedsUpdate(cart, config, qty) {
    return currentBulkAttribute(cart, config) !== targetBulkAttribute(config, qty);
  }

  function updateBulkQuoteNotice(qty, config) {
    const isBulk = isBulkOrder(qty, config);
    document.querySelectorAll('[data-solara-shipping-bulk]').forEach((el) => {
      el.hidden = !isBulk;
    });
  }

  function formatCartTotal(cents, currency) {
    const moneyFormat =
      window.theme?.moneyWithCurrencyFormat || window.theme?.moneyFormat || '${{amount}}';
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    return formatMoney(cents, currency);
  }

  function updateCartPageTotals(cart) {
    if (!cart || cart.total_price == null) return;

    const formattedTotal = formatCartTotal(cart.total_price, cart.currency);
    document
      .querySelectorAll('#main-cart-footer .totals__total-value, .cart-drawer__footer .totals__total-value')
      .forEach((el) => {
        el.textContent = formattedTotal;
      });

    document.querySelectorAll('#cart-icon-bubble').forEach((el) => {
      if (cart.item_count > 0) {
        el.classList.remove('cart-icon-bubble--empty');
      }
      const countEl = el.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (countEl) {
        countEl.textContent = cart.item_count < 100 ? String(cart.item_count) : '99+';
      }
      const visuallyHidden = el.querySelector('.cart-count-bubble .visually-hidden');
      if (visuallyHidden) {
        visuallyHidden.textContent = `${cart.item_count} items`;
      }
    });
  }

  function renderShippingSummary(cart, config) {
    const qty = countForPolicy(cart, config);
    const feeCents = shippingFeeTotal(cart, config);
    const currency = cart.currency || 'ZAR';
    const bulkHtml = `
      <div class="solara-transport-fees__row solara-transport-fees__row--excluded">
        <span class="solara-transport-fees__label">Shipping</span>
        <span class="solara-transport-fees__value solara-transport-fees__value--excluded">Excluded</span>
      </div>
      <p class="solara-transport-fees__bulk caption">Shipping is <strong>not included</strong> in this order and will be <strong>costed separately</strong> before dispatch.</p>
    `;

    document.querySelectorAll('[data-solara-shipping-summary]').forEach((el) => {
      if (isBulkOrder(qty, config)) {
        el.hidden = false;
        el.innerHTML = bulkHtml;
        return;
      }

      // Checkout rates own the charge — hide legacy fee-product summary rows.
      if (!config.enabled || feeCents <= 0) {
        el.hidden = true;
        el.innerHTML = '';
        return;
      }

      el.hidden = false;
      el.innerHTML = `
        <div class="solara-transport-fees__row">
          <span class="solara-transport-fees__label">Shipping</span>
          <span class="solara-transport-fees__value">${formatMoney(feeCents, currency)}</span>
        </div>
        <p class="solara-transport-fees__note caption">${formatMoney(config.nationwideShippingPriceCents || feeCents, currency)} flat rate outside Western Cape.</p>
      `;
    });

    updateCartPageTotals(cart);
  }

  function getCartFooterSectionId() {
    const footerEl = document.getElementById('main-cart-footer');
    return footerEl?.dataset?.id || 'cart-footer';
  }

  function refreshCartFooter() {
    if (!window.routes?.cart_url) return Promise.resolve();

    const sectionId = getCartFooterSectionId();
    return fetch(`${window.routes.cart_url}?section_id=${encodeURIComponent(sectionId)}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceFooter = html.querySelector('#main-cart-footer .js-contents');
        const targetFooter = document.querySelector('#main-cart-footer .js-contents');
        if (sourceFooter && targetFooter) {
          targetFooter.innerHTML = sourceFooter.innerHTML;
        }
      })
      .catch((error) => {
        console.warn('Parasolune shipping: footer refresh failed', error);
      });
  }

  function refreshCartUi() {
    const drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
      drawerItems.onCartUpdate();
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') {
      cartItems.onCartUpdate();
    } else if (document.querySelector('#main-cart-footer .js-contents')) {
      refreshCartFooter();
    }

    if (config?.enabled) {
      window.setTimeout(() => {
        refreshCartFooter().catch(() => {});
        const cartItemsDelayed = document.querySelector('cart-items');
        if (cartItemsDelayed && typeof cartItemsDelayed.onCartUpdate === 'function') {
          cartItemsDelayed.onCartUpdate();
        }
      }, 300);
    }

    document.dispatchEvent(new CustomEvent('solara-shipping-updated'));
  }

  class SolaraTransportFees {
    constructor(config) {
      this.config = config;
      this.syncing = false;
      this.pending = false;
      this.checkoutPending = false;
      this.bindEvents();
      this.scheduleSync();
    }

    bindEvents() {
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === SOURCE) return;
          this.scheduleSync();
        });
      }

      document.addEventListener('DOMContentLoaded', () => this.scheduleSync());
      document.addEventListener('cartUpdated', () => this.scheduleSync());

      document.addEventListener('click', (event) => {
        if (event.target.closest('#cart-icon-bubble')) {
          window.setTimeout(() => this.scheduleSync(), 200);
        }
      });

      document.addEventListener(
        'submit',
        (event) => {
          const submitter = event.submitter;
          if (!submitter || submitter.name !== 'checkout') return;

          event.preventDefault();
          event.stopPropagation();
          this.proceedToCheckout(submitter);
        },
        true
      );
    }

    async proceedToCheckout(submitter) {
      if (this.checkoutPending) return;
      this.checkoutPending = true;

      if (submitter) {
        submitter.setAttribute('aria-busy', 'true');
        submitter.disabled = true;
      }

      try {
        await this.ensureSynced();
        window.location.assign('/checkout');
      } catch (error) {
        console.warn('Parasolune shipping: checkout redirect failed', error);
        window.location.assign('/checkout');
      }
    }

    scheduleSync() {
      if (this.pending) return;
      this.pending = true;
      window.setTimeout(() => {
        this.pending = false;
        this.sync();
      }, 150);
    }

    async ensureSynced() {
      await this.sync();
      while (this.syncing) {
        await new Promise((resolve) => window.setTimeout(resolve, 50));
      }
      return this.sync(true);
    }

    buildUpdatePayload(cart, config) {
      const { desired, qty } = desiredVariantMap(config, cart);
      const current = currentVariantMap(cart, config);
      const payload = {};
      let changed = false;

      if (!mapsEqual(desired, current)) {
        payload.updates = {};
        Object.entries(desired).forEach(([variantId, quantity]) => {
          payload.updates[variantId] = quantity;
        });
        changed = true;
      }

      if (bulkAttributeNeedsUpdate(cart, config, qty)) {
        payload.attributes = {
          ...(cart.attributes || {}),
          [config.bulkOrderAttributeKey]: targetBulkAttribute(config, qty),
        };
        changed = true;
      }

      return { payload, changed, qty };
    }

    async sync(forceRefresh) {
      if (this.syncing) return null;
      if (!window.routes?.cart_url || !window.routes?.cart_update_url) return null;

      this.syncing = true;

      try {
        const cart = await fetch(`${window.routes.cart_url}.js`).then((response) => response.json());
        const { payload, changed, qty } = this.buildUpdatePayload(cart, this.config);

        updateBulkQuoteNotice(qty, this.config);
        renderShippingSummary(cart, this.config);

        if (changed) {
          const updatedCart = await fetch(`${window.routes.cart_update_url}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(payload),
          }).then((response) => response.json());

          if (updatedCart?.status === 'bad_request' || updatedCart?.description) {
            console.warn('Parasolune shipping: cart update failed', updatedCart);
            return cart;
          }

          const updatedQty = countShippable(updatedCart, this.config);
          updateBulkQuoteNotice(updatedQty, this.config);
          renderShippingSummary(updatedCart, this.config);
          refreshCartUi();

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
            publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: SOURCE,
              cartData: updatedCart,
            });
          }

          return updatedCart;
        }

        if (forceRefresh) {
          refreshCartUi();
        }

        return cart;
      } catch (error) {
        console.warn('Parasolune shipping: sync error', error);
        return null;
      } finally {
        this.syncing = false;
      }
    }
  }

  const config = readConfig();
  if (config) {
    window.solaraTransportFees = new SolaraTransportFees(config);
  }
})();solara-transport-fees.js

(function () {
  function applySolaraCartEstimate() {
    var root = document.querySelector("[data-solara-delivery-zone-note]");
    if (!root) return;
    var select = root.querySelector("[data-solara-delivery-zone-select]");
    var wc = select && select.value === "wc";
    var total = wc ? root.getAttribute("data-total-wc") : root.getAttribute("data-total-other");
    var amount = root.querySelector("[data-solara-shipping-amount]");
    var note = root.querySelector("[data-solara-shipping-note]");
    if (amount) amount.textContent = wc ? root.getAttribute("data-ship-wc") : root.getAttribute("data-ship-other");
    if (note) {
      note.textContent = wc
        ? root.getAttribute("data-note-wc") || "Western Cape shipping is free. Estimated total is your cart subtotal."
        : root.getAttribute("data-note-other") || "R350 shipping is included in the estimated total. Western Cape stays free.";
    }
    if (total) {
      document.querySelectorAll(".totals__total-value").forEach(function (el) {
        el.textContent = total;
      });
    }
    if (select && !select.dataset.solaraEstimateBound) {
      select.dataset.solaraEstimateBound = "1";
      select.addEventListener("change", applySolaraCartEstimate);
    }
  }
  window.applySolaraCartEstimate = applySolaraCartEstimate;
  document.addEventListener("DOMContentLoaded", applySolaraCartEstimate);
  document.addEventListener("solara-shipping-updated", applySolaraCartEstimate);
  document.addEventListener("cart:updated", applySolaraCartEstimate);
  if (typeof subscribe === "function" && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(applySolaraCartEstimate, 30);
    });
  }
  [0, 200, 500, 900, 1600, 2500].forEach(function (ms) {
    setTimeout(applySolaraCartEstimate, ms);
  });
})();
